import { carrierName } from '../console/carriers.ts';
import { field, meter, pill } from '../console/components.ts';
import { date, esc, icon, PRODUCT, person, when } from '../console/format.ts';
import { selectControl, textControl } from '../controls.ts';

/**
 * «Seguimiento del reclutamiento» in «Onboarding» (#319): the search and filters of the
 * candidates, their current step and days in it, the recruiting funnel of the quarter and
 * «Por recertificar». Only markup: the view loads the data and binds the actions.
 */

/** GET /v1/onboardings/funnel. */
export type FunnelView = {
  from: string;
  to: string;
  applied: number;
  inProgress: number;
  active: number;
  rejected: number;
  deactivated: number;
  conversion: number;
  averageTotalDays: number | null;
  stages: {
    step: string;
    candidates: number;
    averageDays: number | null;
    deadlineDays: number;
    overdue: number;
  }[];
  bySource: { source: string; applied: number; active: number; conversion: number }[];
  undated: number;
  quarter: { quarter: number; year: number } | null;
};

/** GET /v1/recertifications. */
export type RecertificationView = {
  onboardingId: string;
  fullName: string;
  agentId: string | null;
  carrier: string;
  kind: 'SIB' | 'TRAINING';
  expiresOn: string | null;
  daysLeft: number | null;
  state: 'EXPIRED' | 'DUE' | 'UNDATED';
  course?: string;
};

/** The list's search and filters, kept while the person stays on «Onboarding». */
export type OnboardingFilter = {
  q: string;
  status: string;
  carrier: string;
  productLine: string;
  agent: string;
  olderThanDays: string;
};
export const NO_FILTER: OnboardingFilter = {
  q: '',
  status: '',
  carrier: '',
  productLine: '',
  agent: '',
  olderThanDays: '',
};
export const filtering = (f: OnboardingFilter) => Object.values(f).some((v) => v.trim() !== '');

/** "?q=…&status=…" for GET /v1/onboardings (nothing without filters). */
export function onboardingQuery(f: OnboardingFilter): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(f)) if (v.trim()) q.set(k, v.trim());
  const s = q.toString();
  return s ? `?${s}` : '';
}

const STATUS_OPTIONS: [string, string][] = [
  ['IN_PROGRESS', 'En proceso'],
  ['ACTIVE', 'Agente activo'],
  ['REJECTED', 'Rechazado'],
  ['DEACTIVATED', 'De baja'],
];
const AGE_OPTIONS: [string, string][] = [
  ['7', 'Más de 7 días'],
  ['15', 'Más de 15 días'],
  ['30', 'Más de 30 días'],
  ['60', 'Más de 60 días'],
];

/** "17 días", "1 día", "sin fecha". */
export const daysText = (n: number | null | undefined) =>
  n === null || n === undefined ? 'sin fecha' : `${n} ${n === 1 ? 'día' : 'días'}`;

/** The search and filters above the candidates. */
export function filtersBar(
  f: OnboardingFilter,
  carriers: { id: string; name: string; productLines: string[] }[],
  searchByDpi: boolean,
) {
  const lines = [...new Set(carriers.flatMap((c) => c.productLines))];
  const opt = (value: string, label: string, current: string) => ({
    value,
    label,
    selected: current === value,
  });
  return `<form id="onb-search-form" class="card toolbar policy-filters onb-filters" role="search" aria-label="Buscar candidatos" data-component="onboarding-filters">
    <div class="field grow">${textControl({ label: searchByDpi ? 'Buscar por nombre, DPI o correo' : 'Buscar por nombre o correo', id: 'onb-q', type: 'search', maxlength: 120, autocomplete: 'off', value: f.q, placeholder: searchByDpi ? 'Sofía Méndez · 2451839200101 · correo' : 'Sofía Méndez · correo' })}</div>
    ${field('Estado', selectControl({ id: 'onb-f-status' }, [opt('', 'Todos', f.status), ...STATUS_OPTIONS.map(([v, l]) => opt(v, l, f.status))]))}
    ${field('Aseguradora', selectControl({ id: 'onb-f-carrier' }, [opt('', 'Todas', f.carrier), ...carriers.map((c) => opt(c.id, c.name || carrierName(c.id), f.carrier))]))}
    ${field('Ramo', selectControl({ id: 'onb-f-line' }, [opt('', 'Todos', f.productLine), ...lines.map((l) => opt(l, PRODUCT[l] ?? l, f.productLine))]))}
    ${field('Antigüedad', selectControl({ id: 'onb-f-age' }, [opt('', 'Cualquiera', f.olderThanDays), ...AGE_OPTIONS.map(([v, l]) => opt(v, l, f.olderThanDays))]))}
    ${field('Agente asignado', textControl({ id: 'onb-f-agent', maxlength: 40, autocomplete: 'off', value: f.agent, placeholder: 'Código: marta' }))}
    <div class="order-buttons"><button class="btn primary" type="submit" id="onb-search">Buscar</button>${filtering(f) ? '<button type="button" class="btn" data-onb-clear>Ver todos</button>' : ''}</div></form>`;
}

type Row = {
  id: string;
  candidate: { fullName: string; source: string };
  carrier: string;
  status: string;
  appointment: { agentId: string } | null;
  provisional?: { agentId: string } | null;
  currentStep: { step: string; days: number | null; deadlineDays: number; overdue: boolean } | null;
};

/** The current step of a candidate, with its days and whether it is past its deadline. */
export function currentStepText(o: Row, label: (step: string) => string) {
  const c = o.currentStep;
  if (!c) return '<span class="muted">—</span>';
  return `<span>${esc(label(c.step))}</span>`;
}

export function daysCell(o: Row) {
  const c = o.currentStep;
  if (!c) return '<span class="muted">—</span>';
  return `<span class="onb-days">${esc(daysText(c.days))}${c.overdue ? ` ${pill('bad', `Fuera de plazo (${c.deadlineDays} d)`)}` : ''}</span>`;
}

/** The candidates in a table: who, carrier, current step, days in it, status. */
export function candidatesTable(
  list: Row[],
  label: (step: string) => string,
  status: Record<string, [string, string]>,
) {
  return `<div class="table-wrap" data-component="table"><table class="table onb-table"><caption class="sr-only">Candidatos con su paso actual y los días en ese paso</caption>
    <thead><tr><th scope="col">Candidato</th><th scope="col">Aseguradora</th><th scope="col">Agente</th><th scope="col">Paso actual</th><th scope="col" class="num">Días en el paso</th><th scope="col">Estado</th></tr></thead><tbody>
    ${list
      .map((o) => {
        const [cls, text] = status[o.status] ?? ['info', o.status];
        const code = o.appointment?.agentId ?? o.provisional?.agentId ?? null;
        return `<tr data-onb-row="${esc(o.id)}"><td><strong>${esc(o.candidate.fullName)}</strong><span class="policy-sub">${esc(o.candidate.source)}</span></td><td>${esc(carrierName(o.carrier))}</td>
        <td>${code ? `<span class="mono">${esc(code)}</span>` : '<span class="muted">—</span>'}</td><td>${currentStepText(o, label)}</td><td class="num">${daysCell(o)}</td><td>${pill(cls, text)}</td></tr>`;
      })
      .join('')}</tbody></table></div>`;
}

const pct = (r: number) => `${Math.round(r * 100)} %`;
const QUARTER = ['1.er', '2.º', '3.er', '4.º'];

/** «Embudo de reclutamiento»: per stage, conversion, average times, overdue, per source. */
export function funnelCard(f: FunnelView | null, label: (step: string) => string) {
  if (!f)
    return `<section class="card" aria-labelledby="onb-funnel-title"><div class="card-head">${icon('target')}<h2 id="onb-funnel-title">Embudo de reclutamiento</h2></div>
      <p class="note">${icon('alert')}<span>No se pudo cargar el embudo del trimestre.</span><button type="button" class="btn sm" data-retry>Reintentar</button></p></section>`;
  const period = f.quarter
    ? `${QUARTER[f.quarter.quarter - 1] ?? ''} trimestre ${f.quarter.year}`
    : `${date(f.from)} al ${date(f.to)}`;
  const most = Math.max(1, ...f.stages.map((s) => s.candidates));
  const rows = f.stages
    .map(
      (s) => `<tr><td>${esc(label(s.step))}</td>
      <td class="num"><span class="onb-funnel-bar">${meter(s.candidates / most, 'inline')}<b>${s.candidates}</b></span></td>
      <td class="num">${s.averageDays === null ? '<span class="muted">—</span>' : esc(daysText(s.averageDays))}</td>
      <td class="num">${esc(daysText(s.deadlineDays))}</td>
      <td class="num">${s.overdue ? pill('bad', String(s.overdue)) : '<span class="muted">0</span>'}</td></tr>`,
    )
    .join('');
  const sources = f.bySource.length
    ? `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Conversión por fuente de reclutamiento</caption><thead><tr><th scope="col">Fuente</th><th scope="col" class="num">Postulaciones</th><th scope="col" class="num">Agentes activos</th><th scope="col" class="num">Conversión</th></tr></thead><tbody>
      ${f.bySource.map((s) => `<tr><td><strong>${esc(s.source)}</strong></td><td class="num">${s.applied}</td><td class="num">${s.active}</td><td class="num">${pct(s.conversion)}</td></tr>`).join('')}</tbody></table></div>`
    : '';
  return `<section class="card" aria-labelledby="onb-funnel-title" data-component="onboarding-funnel">
    <div class="card-head">${icon('target')}<h2 id="onb-funnel-title">Embudo de reclutamiento</h2><span class="spacer"></span><span class="muted small">${esc(period)} · expedientes abiertos en el período</span></div>
    <div class="onb-funnel-summary">
      <div><span class="kpi-label">Postulaciones</span><span class="onb-funnel-value">${f.applied}</span></div>
      <div><span class="kpi-label">Agentes activos</span><span class="onb-funnel-value">${f.active}</span></div>
      <div><span class="kpi-label">Conversión</span><span class="onb-funnel-value">${pct(f.conversion)}</span></div>
      <div><span class="kpi-label">Tiempo medio al alta</span><span class="onb-funnel-value">${f.averageTotalDays === null ? '—' : esc(daysText(f.averageTotalDays))}</span></div>
    </div>
    ${
      f.applied
        ? `<div class="table-wrap" data-component="table"><table class="table onb-funnel"><caption class="sr-only">Candidatos por etapa, tiempo medio y pasos fuera de plazo</caption><thead><tr><th scope="col">Etapa</th><th scope="col" class="num">Candidatos</th><th scope="col" class="num">Tiempo medio</th><th scope="col" class="num">Plazo</th><th scope="col" class="num">Fuera de plazo</th></tr></thead><tbody>${rows}</tbody></table></div>
    ${sources}`
        : `<p class="muted small card-pad">Ningún expediente se abrió en este trimestre.</p>`
    }
    <p class="muted small card-pad">El tiempo medio cuenta los pasos ya completados, desde que se abren (al completarse lo que exigen). ${f.rejected} rechazados · ${f.deactivated} de baja.${f.undated ? ` ${f.undated} ${f.undated === 1 ? 'expediente sin fecha queda' : 'expedientes sin fecha quedan'} fuera del embudo.` : ''}</p>
  </section>`;
}

const RECERT_KIND: Record<string, string> = {
  SIB: 'Autorización SIB',
  TRAINING: 'Capacitación',
};

/** «Por recertificar»: expired and expiring (30 days) SIB authorizations and trainings. */
export function recertificationsCard(list: RecertificationView[] | null) {
  const head = `<div class="card-head">${icon('calendar')}<h2 id="onb-recert-title">Por recertificar</h2><span class="spacer"></span><span class="muted small">Vencidas y por vencer en 30 días · se avisa a la dirección y a la administración</span></div>`;
  if (!list)
    return `<section class="card" aria-labelledby="onb-recert-title">${head}<p class="note">${icon('alert')}<span>No se pudo cargar la lista por recertificar.</span><button type="button" class="btn sm" data-retry>Reintentar</button></p></section>`;
  const dated = list.filter((r) => r.state !== 'UNDATED');
  const undated = list.filter((r) => r.state === 'UNDATED');
  const rows = dated
    .map(
      (
        r,
      ) => `<tr><td><strong>${esc(r.fullName)}</strong></td><td>${r.agentId ? `<span class="mono">${esc(r.agentId)}</span>` : '—'}</td>
      <td>${esc(RECERT_KIND[r.kind] ?? r.kind)}${r.course ? `<span class="policy-sub">${esc(r.course)}</span>` : ''}</td>
      <td>${esc(date(r.expiresOn))}</td>
      <td>${r.state === 'EXPIRED' ? pill('bad', `Vencida hace ${daysText(-(r.daysLeft ?? 0))}`) : pill('warn', r.daysLeft === 0 ? 'Vence hoy' : `Vence en ${daysText(r.daysLeft)}`)}</td></tr>`,
    )
    .join('');
  return `<section class="card" aria-labelledby="onb-recert-title" data-component="recertifications">${head}
    ${
      dated.length
        ? `<div class="table-wrap" data-component="table"><table class="table"><caption class="sr-only">Agentes por recertificar</caption><thead><tr><th scope="col">Agente</th><th scope="col">Código</th><th scope="col">Qué vence</th><th scope="col">Vence</th><th scope="col">Estado</th></tr></thead><tbody>${rows}</tbody></table></div>`
        : `<p class="empty-inline card-pad">${icon('ok')}<span>Ningún agente activo tiene la autorización SIB o la capacitación vencida o por vencer en 30 días.</span></p>`
    }
    ${undated.length ? `<p class="muted small card-pad">${icon('alert')} Sin vencimiento SIB registrado: ${undated.map((r) => esc(r.fullName)).join(', ')}. Regístralo en su tarjeta para que Nubi avise.</p>` : ''}
  </section>`;
}

type FichaView = {
  candidate: {
    privacy?: { notice: string; acceptedAt: string };
    biometricCheck?: { score: number; reference: string; verifiedAt: string };
  };
  biometrics: { result: 'MATCH' | 'NO_MATCH'; at: string | null; score?: number } | null;
  appointment: {
    carrier?: string;
    productLines: string[];
    since: string;
    until: string | null;
    terms: {
      split?: string;
      commissions?: Record<string, string>;
      regime: string;
      nit?: string;
    } | null;
  } | null;
  reactivation: { at: string; by: string; note: string; renewed: string[] } | null;
};

const REGIME: Record<string, string> = {
  SIMPLIFIED: 'Pequeño contribuyente simplificado',
  PROFITS: 'Sobre utilidades',
  SMALL_TAXPAYER: 'Pequeño contribuyente',
};

/**
 * The candidate's record: the privacy notice they accepted and when, the biometric result
 * (its score for who runs onboarding) and the terms of the appointment (read only).
 */
export function fichaBlock(o: FichaView, detail: boolean) {
  const items: string[] = [];
  if (detail)
    items.push(
      `<div><dt>Aviso de privacidad</dt><dd>${o.candidate.privacy ? `Aceptado el ${esc(when(o.candidate.privacy.acceptedAt))} <span class="muted small">(${esc(o.candidate.privacy.notice)})</span>` : '<span class="muted">Sin aceptación en Nubi (lo registró la agencia o el portal)</span>'}</dd></div>`,
    );
  const b = o.biometrics;
  const score = o.biometrics?.score ?? o.candidate.biometricCheck?.score;
  items.push(
    `<div><dt>Biometría (RENAP)</dt><dd>${
      b
        ? `${b.result === 'MATCH' ? pill('ok', 'Coincide') : pill('bad', 'No coincide')}${detail && score !== undefined ? ` <span class="small">Puntaje <b class="mono">${esc(String(score))}</b></span>` : ''}${b.at ? ` <span class="muted small">${esc(when(b.at))}</span>` : ''}`
        : '<span class="muted">Sin validar</span>'
    }</dd></div>`,
  );
  const a = o.appointment;
  if (a) {
    const t = a.terms;
    const commission = t?.commissions
      ? Object.entries(t.commissions)
          .map(([l, v]) => `${esc(PRODUCT[l] ?? l)} ${esc(v)} %`)
          .join(' · ')
      : t?.split
        ? `${esc(t.split)} % en todos los ramos`
        : '—';
    items.push(
      `<div><dt>Nombramiento</dt><dd>Desde el ${esc(date(a.since))}${a.until ? ` hasta el ${esc(date(a.until))}` : ''} · ${a.productLines.map((l) => esc(PRODUCT[l] ?? l)).join(', ')}</dd></div>`,
      `<div><dt>Comisión del agente</dt><dd>${commission}</dd></div>`,
      `<div><dt>Régimen</dt><dd>${t ? esc(REGIME[t.regime] ?? t.regime) : '—'}${detail && t?.nit ? ` · NIT <span class="mono">${esc(t.nit)}</span>` : ''}</dd></div>`,
    );
  }
  if (o.reactivation)
    items.push(
      `<div><dt>Reactivado</dt><dd>${esc(when(o.reactivation.at))} · ${esc(person(o.reactivation.by))} · «${esc(o.reactivation.note)}»</dd></div>`,
    );
  return `<dl class="onb-ficha" aria-label="Ficha del candidato">${items.join('')}</dl>`;
}
